import { action, makeObservable, observable } from 'mobx';

import type {
  TClassOfUnitData,
  TDictionaryFieldUpdates,
  TDictionarySettings,
  TUnitsSettingsState,
  TUomData,
} from './types';
import type { AxiosInstance } from 'axios';
import type {
  IDirectoriesStorage,
  TStorageObject,
} from 'src/services/directories-storage-service/directories-storage-service';

import { UnitsSettingsApi } from './units-settings-api';

const CLASS_OF_UNIT_OBJECT_TYPE = 'Common_ClassOfUnit';
const UOM_OBJECT_TYPE = 'Common_Uom';

export class UnitsSettingsService {
  private readonly api: UnitsSettingsApi;
  private readonly patchAbortControllers = new Map<string, AbortController>();
  private readonly states = observable.map<string, TUnitsSettingsState>();

  constructor(
    agent: AxiosInstance,
    private readonly directoriesStorage: IDirectoriesStorage,
  ) {
    this.api = new UnitsSettingsApi(agent);

    makeObservable(this);
  }

  getState(objectType: string): TUnitsSettingsState {
    return (
      this.states.get(objectType) ?? {
        status: 'disabled',
        settings: null,
        classesOfUnit: [],
        units: [],
      }
    );
  }

  async initialize(objectType: string): Promise<void> {
    const state = this.states.get(objectType);

    if (state && state.status !== 'error') {
      return;
    }

    await this.initializeState(objectType);
  }

  async patchFields(objectType: string, fields: TDictionaryFieldUpdates): Promise<'applied' | 'aborted'> {
    const currentState = this.states.get(objectType);

    if (!currentState || currentState.status !== 'ready' || !currentState.settings) {
      throw new Error(`Unit settings for ${objectType} are not ready`);
    }

    this.patchAbortControllers.get(objectType)?.abort();

    const abortController = new AbortController();
    this.patchAbortControllers.set(objectType, abortController);

    try {
      const settings = await this.api.patchSettings(objectType, { fields }, abortController.signal);

      if (this.patchAbortControllers.get(objectType) !== abortController) {
        return 'aborted';
      }

      if (settings.objectType && settings.objectType !== objectType) {
        throw new Error(`Received unit settings for ${settings.objectType} instead of ${objectType}`);
      }

      this.applyPatchedSettings(objectType, settings);

      return 'applied';
    } catch (error) {
      if (abortController.signal.aborted || this.patchAbortControllers.get(objectType) !== abortController) {
        return 'aborted';
      }

      throw error;
    } finally {
      if (this.patchAbortControllers.get(objectType) === abortController) {
        this.patchAbortControllers.delete(objectType);
      }
    }
  }

  private async initializeState(objectType: string): Promise<void> {
    this.updateState(objectType, { status: 'loading' });

    try {
      const settings = await this.api.loadSettings(objectType);

      if (settings.objectType && settings.objectType !== objectType) {
        throw new Error(`Received unit settings for ${settings.objectType} instead of ${objectType}`);
      }

      if (!Object.keys(settings.fields ?? {}).length) {
        this.updateState(objectType, { status: 'disabled', settings });
        return;
      }

      const { classesOfUnit, units } = await this.loadCatalogs();

      this.updateState(objectType, { status: 'ready', settings, classesOfUnit, units });
    } catch (error) {
      console.error(`Failed to load unit settings for ${objectType}`, error);
      this.updateState(objectType, { status: 'error' });
    }
  }

  private async loadCatalogs(): Promise<{
    classesOfUnit: ReadonlyArray<TStorageObject<TClassOfUnitData>>;
    units: ReadonlyArray<TStorageObject<TUomData>>;
  }> {
    const [classesOfUnit, units] = await Promise.all([
      this.directoriesStorage.loadObjects<TClassOfUnitData>(CLASS_OF_UNIT_OBJECT_TYPE, true),
      this.directoriesStorage.loadObjects<TUomData>(UOM_OBJECT_TYPE, true),
    ]);

    return { classesOfUnit, units };
  }

  @action.bound
  private updateState(
    objectType: string,
    state: Pick<TUnitsSettingsState, 'status'> & Partial<Omit<TUnitsSettingsState, 'status'>>,
  ): void {
    this.states.set(objectType, {
      status: state.status,
      settings: state.settings ?? null,
      classesOfUnit: state.classesOfUnit ?? [],
      units: state.units ?? [],
    });
  }

  @action.bound
  private applyPatchedSettings(objectType: string, settings: TDictionarySettings): void {
    const currentState = this.states.get(objectType);

    if (!currentState || currentState.status !== 'ready') {
      throw new Error(`Cannot apply unit settings for ${objectType} before initialization`);
    }

    this.states.set(objectType, { ...currentState, settings });
  }
}
